import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  HandGrabbing, Truck, ArrowRight, ArrowsCounterClockwise,
  ArrowsLeftRight, Tray, ListChecks, Stack, MagnifyingGlass, X, PaperPlaneTilt, DownloadSimple, Printer,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printTruckManifest } from '../../lib/api';
import HelpAnchor from '../HelpAnchor';

const STORAGE_KEY = 'operator.queue.category';

const CATEGORIES = [
  { key: 'all', label: 'All', Icon: Stack, color: 'bg-zinc-700 text-white border-zinc-700' },
  { key: 'pick', label: 'Picking', Icon: HandGrabbing, color: 'bg-brand text-white border-brand' },
  { key: 'ship', label: 'Pack & Ship', Icon: Truck, color: 'bg-green-600 text-white border-green-600' },
  { key: 'receive', label: 'Receiving', Icon: Tray, color: 'bg-blue-600 text-white border-blue-600' },
  { key: 'transfer_out', label: 'Transfers OUT', Icon: PaperPlaneTilt, color: 'bg-emerald-600 text-white border-emerald-600' },
  { key: 'transfer_in', label: 'Transfers IN', Icon: DownloadSimple, color: 'bg-sky-600 text-white border-sky-600' },
  { key: 'move', label: 'Stock Moves', Icon: ArrowsLeftRight, color: 'bg-amber-500 text-white border-amber-500' },
  { key: 'count', label: 'Cycle Counts', Icon: ListChecks, color: 'bg-violet-500 text-white border-violet-500' },
];

/**
 * QueueView — the landing screen of the Operator Console.
 * Lists open work across pick / ship / receive / move / count and
 * delegates entry to handlers from the parent (which holds shared data).
 *
 * Category filter (iter130): operators can collapse the view to a single
 * work category. Last selection is persisted in localStorage so an
 * RF-gun operator returning from a scan doesn't have to re-pick the
 * filter every time.
 */
export default function QueueView({
  header,
  loading,
  busy,
  isAdmin,
  pickLists,
  readyOrders,
  pendingReceipts,
  pendingMoves,
  pendingCounts,
  onRefresh,
  onStartPick,
  onStartShip,
  onStartReceive,
  onStartMove,
  onExecutePlannedMove,
  onStartCount,
  onStartQuickCount,
}) {
  const [category, setCategory] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'all'; }
    catch { return 'all'; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, category); } catch { /* ignore */ }
  }, [category]);

  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const matches = (...vals) => !q || vals.some((v) => (v || '').toString().toLowerCase().includes(q));

  // Pre-filter every queue by the search query (job-number / order-number /
  // any other identifier the operator might know off a pick ticket).
  // Transfer picks/receipts get their own dedicated sections in the UI —
  // customer picks/receipts are split out so they don't appear twice.
  const filteredPicks = useMemo(
    () => pickLists.filter((p) => p.source_type !== 'transfer' && matches(p.pick_list_number, ...(p.order_ids || []))),
    [pickLists, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredTransferPicks = useMemo(
    () => pickLists.filter((p) => p.source_type === 'transfer' && matches(p.pick_list_number, p.transfer_number, p.route_label)),
    [pickLists, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredShip = useMemo(
    () => readyOrders.filter((o) => matches(o.order_number, o.customer_name, o.shipping_address)),
    [readyOrders, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredReceipts = useMemo(
    () => pendingReceipts.filter((r) => r.source_type !== 'transfer' && matches(r.receipt_number, r.po_number, r.supplier_name, r.vendor_name)),
    [pendingReceipts, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredTransferReceipts = useMemo(
    () => pendingReceipts.filter((r) => r.source_type === 'transfer' && matches(r.receipt_number, r.transfer_number, r.route_label)),
    [pendingReceipts, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredMoves = useMemo(
    () => pendingMoves.filter((m) => matches(m.move_number, m.from_location_code, m.to_location_code)),
    [pendingMoves, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredCounts = useMemo(
    () => pendingCounts.filter((c) => matches(c.count_number, c.count_type)),
    [pendingCounts, q]   // eslint-disable-line react-hooks/exhaustive-deps
  );

  const counts = {
    pick: filteredPicks.length,
    ship: filteredShip.length,
    receive: filteredReceipts.length,
    transfer_out: filteredTransferPicks.length,
    transfer_in: filteredTransferReceipts.length,
    move: filteredMoves.length,
    count: filteredCounts.length,
  };
  const show = (key) => category === 'all' || category === key;

  return (
    <div data-testid="operator-console" className="h-screen bg-zinc-100 flex flex-col overflow-hidden">
      {header}
      <div className="flex-1 p-3 md:p-4 max-w-6xl mx-auto w-full space-y-3 overflow-auto">
        {/* Job-number search — operators can type a pick/order/receipt/move/count
            number prefix to instantly narrow the queue. Auto-clears the
            category filter so a search hit always surfaces. */}
        <div className="relative" data-testid="operator-search-bar">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="operator-search"
            placeholder="Search PICK / order / receipt / move / count number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-10 text-sm font-mono bg-white"
            autoComplete="off"
          />
          {search && (
            <button
              type="button"
              data-testid="operator-search-clear"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 rounded"
              aria-label="Clear search"
            >
              <X size={14} className="text-zinc-500" />
            </button>
          )}
        </div>

        {/* Category filter — large tap targets sized for RF guns + gloves */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-1.5" data-testid="operator-category-bar">
          {CATEGORIES.map(({ key, label, Icon, color }) => {
            const active = category === key;
            const count = key === 'all'
              ? counts.pick + counts.ship + counts.receive + counts.transfer_out + counts.transfer_in + counts.move + counts.count
              : counts[key];
            return (
              <button
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`operator-category-${key}`}
                className={`px-2 py-2 border-2 transition-all flex items-center gap-2 ${
                  active ? color : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400'
                }`}
              >
                <Icon size={18} weight={active ? 'fill' : 'bold'} className="flex-shrink-0" />
                <div className="flex-1 text-left leading-none">
                  <div className="text-[10px] font-black uppercase tracking-wider">{label}</div>
                  <div className="text-base font-black tabular-nums mt-0.5">{count}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Pick queue */}
        {show('pick') && (
        <div data-testid="operator-section-pick">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-black uppercase tracking-widest text-zinc-700">Pick queue ({pickLists.length})</h2>
            <Button onClick={onRefresh} variant="ghost" size="sm" disabled={loading} data-testid="operator-refresh-btn">
              <ArrowsCounterClockwise size={16} className="mr-1" /> Refresh
            </Button>
          </div>
          {loading ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-white animate-pulse" />)}</div>
          ) : filteredPicks.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">{q ? `No pick lists match "${search}".` : 'No open pick lists.'}</div>
          ) : (
            <div className="grid gap-2">
              {filteredPicks.slice(0, 20).map((pl) => {
                const total = pl.items?.length || 0;
                const picked = pl.items?.filter((i) => i.status === 'picked').length || 0;
                return (
                  <button
                    key={pl.id}
                    onClick={() => onStartPick(pl)}
                    disabled={busy}
                    data-testid={`operator-start-pick-${pl.pick_list_number}`}
                    className="bg-white border-2 border-zinc-200 hover:border-brand p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <HandGrabbing size={26} weight="bold" className="text-brand flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-black text-base">{pl.pick_list_number}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                        {pl.is_batch ? 'Batch · ' : ''}{total} items · {pl.order_ids?.length || 0} orders
                        {picked > 0 && ` · ${picked} picked`}
                      </div>
                    </div>
                    <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Transfers OUT — pick lists that fulfil inter-warehouse transfers */}
        {show('transfer_out') && (
        <div data-testid="operator-section-transfer-out">
          <h2 className="text-sm font-black uppercase tracking-widest text-emerald-800 mb-2 flex items-center gap-2">
            <PaperPlaneTilt size={16} weight="fill" /> Transfers OUT ({filteredTransferPicks.length})
          </h2>
          {filteredTransferPicks.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">
              {q ? `No transfer picks match "${search}".` : 'No open transfer picks.'}
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredTransferPicks.slice(0, 20).map((pl) => {
                const total = pl.items?.length || 0;
                const picked = pl.items?.filter((i) => i.status === 'picked').length || 0;
                return (
                  <button
                    key={pl.id}
                    onClick={() => onStartPick(pl)}
                    disabled={busy}
                    data-testid={`operator-start-transfer-out-${pl.pick_list_number}`}
                    className="bg-white border-2 border-emerald-200 hover:border-emerald-600 p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <PaperPlaneTilt size={26} weight="bold" className="text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-black text-base">{pl.pick_list_number}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                        {pl.transfer_number ? `Transfer ${pl.transfer_number}` : 'Transfer'}
                        {pl.route_label && <span className="ml-1 text-emerald-700 font-bold">· {pl.route_label}</span>}
                        {total > 0 && ` · ${picked}/${total} picked`}
                      </div>
                    </div>
                    <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Ship queue */}
        {show('ship') && (
        <div data-testid="operator-section-ship">
          <h2 className="text-sm font-black uppercase tracking-widest text-zinc-700 mb-2">Ready to ship ({readyOrders.length})</h2>
          {readyOrders.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">No orders waiting to ship.</div>
          ) : (
            <div className="grid gap-2">
              {readyOrders.slice(0, 20).map((o) => (
                <button
                  key={o.id}
                  onClick={() => onStartShip(o)}
                  data-testid={`operator-start-ship-${o.order_number}`}
                  className="bg-white border-2 border-zinc-200 hover:border-green-600 p-3 text-left transition-colors flex items-center gap-3"
                >
                  <Truck size={26} weight="bold" className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-black text-base">{o.order_number}</div>
                    <div className="text-xs text-zinc-700 truncate">{o.customer_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{o.shipping_address}</div>
                  </div>
                  <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Receiving */}
        {show('receive') && (
        <div data-testid="operator-section-receive">
          <h2 className="text-sm font-black uppercase tracking-widest text-zinc-700 mb-2">Inbound receipts ({pendingReceipts.length})</h2>
          {filteredReceipts.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">{q ? `No receipts match "${search}".` : 'No pending receipts.'}</div>
          ) : (
            <div className="grid gap-2">
              {filteredReceipts.slice(0, 10).map((r) => {
                const total = (r.items || []).length;
                const recvd = (r.items || []).filter((i) => (i.received_quantity || 0) > 0).length;
                return (
                  <button
                    key={r.id}
                    onClick={() => onStartReceive(r)}
                    disabled={busy}
                    data-testid={`operator-start-receive-${r.receipt_number}`}
                    className="bg-white border-2 border-zinc-200 hover:border-blue-600 p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <Tray size={26} weight="bold" className="text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-black text-base">{r.receipt_number}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                        {r.po_number ? `PO ${r.po_number} · ` : ''}
                        {r.supplier_name || r.vendor_name || 'Vendor'}
                        {total > 0 && ` · ${recvd}/${total} lines`}
                        {r.status === 'in_progress' && <span className="ml-2 text-amber-600">In progress</span>}
                      </div>
                    </div>
                    <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Transfers IN — inbound receipts that arrive from other warehouses */}
        {show('transfer_in') && (
        <div data-testid="operator-section-transfer-in">
          <h2 className="text-sm font-black uppercase tracking-widest text-sky-800 mb-2 flex items-center gap-2">
            <DownloadSimple size={16} weight="fill" /> Transfers IN ({filteredTransferReceipts.length})
            <HelpAnchor articleId="truck-manifest" label="How Dock bundles + Print manifest works" className="ml-1" />
          </h2>
          {filteredTransferReceipts.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">
              {q ? `No transfer receipts match "${search}".` : 'No inbound transfers waiting.'}
            </div>
          ) : (() => {
            // Truck Load Planner — bundle receipts that share the same
            // ETA date + destination WH into a single "truck" row so ops
            // sees 'Dock 1 · Today · 4 transfers · 240 units' instead of
            // four separate cards. Rows without an ETA fall into their
            // own "No ETA" bucket at the bottom.
            const sorted = [...filteredTransferReceipts].sort((a, b) => {
              const ea = a.estimated_arrival ? new Date(a.estimated_arrival).getTime() : Infinity;
              const eb = b.estimated_arrival ? new Date(b.estimated_arrival).getTime() : Infinity;
              return ea - eb;
            });
            const bundles = new Map();
            sorted.slice(0, 50).forEach((r) => {
              const dateKey = r.estimated_arrival
                ? new Date(r.estimated_arrival).toISOString().slice(0, 10)
                : 'no-eta';
              const key = `${dateKey}|${r.warehouse_id || 'all'}`;
              if (!bundles.has(key)) {
                bundles.set(key, {
                  dateKey,
                  warehouseCode: r.route_label ? r.route_label.split('→').pop().trim() : null,
                  receipts: [],
                });
              }
              bundles.get(key).receipts.push(r);
            });

            const now = Date.now();
            const bundleLabel = (dateKey) => {
              if (dateKey === 'no-eta') return { label: 'No ETA yet', cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' };
              const d = new Date(dateKey + 'T12:00');
              const days = Math.floor((d.getTime() - now) / 86400000);
              if (days < 0) return { label: `Overdue · ${dateKey}`, cls: 'bg-rose-100 text-rose-800 border-rose-300' };
              if (days === 0) return { label: `Today · ${dateKey}`, cls: 'bg-amber-100 text-amber-800 border-amber-300' };
              if (days === 1) return { label: `Tomorrow · ${dateKey}`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
              return { label: `in ${days}d · ${dateKey}`, cls: 'bg-sky-50 text-sky-800 border-sky-200' };
            };

            const printManifest = async (bundle, bIdx) => {
              const { label } = bundleLabel(bundle.dateKey);
              try {
                const r = await printTruckManifest({
                  receipt_ids: bundle.receipts.map((x) => x.id),
                  dock_label: `Dock ${bIdx + 1}`,
                  eta_label: label,
                  warehouse_label: bundle.warehouseCode || '',
                });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `truck-manifest-dock-${bIdx + 1}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Manifest ready');
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not print manifest');
              }
            };

            return (
              <div className="grid gap-3" data-testid="transfer-in-bundles">
                {[...bundles.values()].map((bundle, bIdx) => {
                  const { label, cls } = bundleLabel(bundle.dateKey);
                  const totalUnits = bundle.receipts.reduce(
                    (s, r) => s + (r.expected_items || []).reduce((a, i) => a + (i.expected_qty || 0), 0),
                    0
                  );
                  const isBundle = bundle.receipts.length > 1;
                  const bundleKey = `${bundle.dateKey}-${bundle.warehouseCode || 'x'}`;
                  return (
                    <div key={bundleKey} className="bg-white border border-sky-200 overflow-hidden" data-testid={`transfer-in-bundle-${bundleKey}`}>
                      <div className={`flex items-center justify-between px-3 py-2 border-b border-sky-200 text-xs font-black uppercase tracking-widest ${cls}`}>
                        <span className="flex items-center gap-2">
                          <Truck size={14} weight="fill" />
                          Dock {bIdx + 1} · {label}
                          {bundle.warehouseCode && <span className="ml-1 font-mono">· {bundle.warehouseCode}</span>}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="font-mono">
                            {bundle.receipts.length} transfer{bundle.receipts.length === 1 ? '' : 's'} · {totalUnits} units
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); printManifest(bundle, bIdx); }}
                            data-testid={`operator-print-truck-manifest-${bundleKey}`}
                            title="Print truck manifest (PDF)"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-current/40 hover:bg-white/40 transition-colors text-[10px] font-black uppercase tracking-widest"
                          >
                            <Printer size={12} weight="fill" /> Print manifest
                          </button>
                        </span>
                      </div>
                      <div className="divide-y divide-sky-100">
                        {bundle.receipts.map((r) => {
                          const expected = (r.expected_items || []).length;
                          const recvd = (r.received_items || []).length;
                          return (
                            <button
                              key={r.id}
                              onClick={() => onStartReceive(r)}
                              disabled={busy}
                              data-testid={`operator-start-transfer-in-${r.receipt_number}`}
                              className="w-full px-3 py-2 text-left hover:bg-sky-50 transition-colors flex items-center gap-3 disabled:opacity-50"
                            >
                              <DownloadSimple size={22} weight="bold" className="text-sky-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono font-black text-sm">
                                  {r.receipt_number}
                                  {isBundle && <span className="ml-2 text-[10px] font-normal text-sky-700">on this truck</span>}
                                </div>
                                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                                  {r.transfer_number ? `Transfer ${r.transfer_number}` : 'Transfer'}
                                  {r.route_label && <span className="ml-1 text-sky-700 font-bold">· {r.route_label}</span>}
                                  {expected > 0 && ` · ${recvd}/${expected} lines`}
                                  {r.auto_allocate && <span className="ml-2 text-emerald-700 font-bold">· AUTO-ALLOC</span>}
                                  {r.status === 'in_progress' && <span className="ml-2 text-amber-600">In progress</span>}
                                </div>
                              </div>
                              <ArrowRight size={20} className="text-zinc-400 flex-shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
        )}

        {/* Stock moves */}
        {show('move') && (
        <div data-testid="operator-section-move">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-black uppercase tracking-widest text-zinc-700">Stock moves ({pendingMoves.length} pending)</h2>
            <Button
              onClick={onStartMove}
              size="sm"
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="operator-start-quick-move"
            >
              <ArrowsLeftRight size={16} className="mr-1" weight="bold" /> Quick Move
            </Button>
          </div>
          {filteredMoves.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">
              {q ? `No moves match "${search}".` : 'No pending moves. Tap "Quick Move" above to start a scan-driven move.'}
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredMoves.slice(0, 10).map((m) => (
                <button
                  key={m.id}
                  onClick={() => onExecutePlannedMove(m)}
                  disabled={busy}
                  data-testid={`operator-execute-move-${m.move_number}`}
                  className="bg-white border-2 border-zinc-200 hover:border-amber-500 p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                >
                  <ArrowsLeftRight size={26} weight="bold" className="text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-black text-lg">{m.move_number}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      <span className="font-mono">{m.from_location_code || '?'}</span>
                      {' → '}
                      <span className="font-mono">{m.to_location_code || '?'}</span>
                      <span className="ml-2">· {m.reason_code}</span>
                      <span className="ml-2">· {(m.items || []).length} item(s)</span>
                    </div>
                    {m.notes && <div className="text-xs text-zinc-400 mt-1 italic truncate">{m.notes}</div>}
                  </div>
                  <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Cycle counts */}
        {show('count') && (
        <div data-testid="operator-section-count">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-black uppercase tracking-widest text-zinc-700">Cycle counts ({pendingCounts.length} pending)</h2>
            {isAdmin && (
              <Button
                onClick={onStartQuickCount}
                size="sm"
                className="bg-violet-500 hover:bg-violet-600"
                disabled={busy}
                data-testid="operator-start-quick-count"
              >
                <ListChecks size={16} className="mr-1" weight="bold" /> Quick Count
              </Button>
            )}
          </div>
          {filteredCounts.length === 0 ? (
            <div className="bg-white p-4 text-center text-zinc-500 text-sm">
              {q ? `No counts match "${search}".` : `No pending counts.${isAdmin ? ' Tap "Quick Count" above to start an ad-hoc count.' : ''}`}
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredCounts.slice(0, 10).map((cc) => {
                const counted = (cc.counted_items || []).length;
                const targetLocs = (cc.location_ids || []).length;
                const targetSkus = (cc.sku_ids || []).length;
                return (
                  <button
                    key={cc.id}
                    onClick={() => onStartCount(cc)}
                    disabled={busy}
                    data-testid={`operator-start-count-${cc.count_number}`}
                    className="bg-white border-2 border-zinc-200 hover:border-violet-500 p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <ListChecks size={26} weight="bold" className="text-violet-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-black text-base">{cc.count_number}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                        {cc.count_type || 'full'}
                        {targetLocs > 0 && ` · ${targetLocs} location(s)`}
                        {targetSkus > 0 && ` · ${targetSkus} SKU(s)`}
                        {counted > 0 && ` · ${counted} counted`}
                        {cc.status === 'in_progress' && <span className="ml-2 text-amber-600">In progress</span>}
                      </div>
                    </div>
                    <ArrowRight size={22} className="text-zinc-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
